import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { apiClient } from '../api/client';
import { toast } from 'sonner';

interface RenameDialogState {
  suggestion: any;
  enabledRunbookIds?: string[];
  defaultName: string;
}

/**
 * Shared host-page actions for the Workflow Assistant chat: saving a chat
 * suggestion to the Library, creating it as a draft workflow (with runbook
 * linking and navigation to the builder), and the 409 duplicate-name rename
 * dialog. Used by the Workflow Library page and the Home page Build tab so
 * the behavior stays identical everywhere the assistant is embedded.
 */
export function useAssistantWorkflowActions(options?: {
  onSaved?: () => void;
  /** Simulated mode: workflows/suggestions created from the chat are marked
   *  simulated (sandboxed, test-only) because no real integration is
   *  connected. */
  simulated?: boolean;
}) {
  const navigate = useNavigate();
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null);
  const [renameName, setRenameName] = useState('');

  const saveSuggestion = async (suggestion: any): Promise<boolean> => {
    try {
      await apiClient.saveSuggestedWorkflow(options?.simulated ? { ...suggestion, simulated: true } : suggestion);
      options?.onSaved?.();
      return true;
    } catch {
      toast.error('Failed to save workflow to Library');
      return false;
    }
  };

  const linkRunbooks = async (enabledRunbookIds: string[] | undefined, workflowId: string) => {
    if (!enabledRunbookIds?.length) return;
    // Only link actual uploaded runbooks (UUIDs), not github-file: or
    // confluence-page: prefixed sources
    const linkableIds = enabledRunbookIds.filter(id => !id.includes(':'));
    if (linkableIds.length > 0) {
      await Promise.all(
        linkableIds.map(rbId => apiClient.linkRunbookToWorkflow(rbId, workflowId).catch(() => {}))
      );
    }
  };

  const createSuggestion = async (suggestion: any, enabledRunbookIds?: string[]) => {
    try {
      // If the suggestion has a full definition (new format from builder-aligned agent),
      // create the workflow directly without re-generating
      if (suggestion.definition?.nodes?.length) {
        const wf = await apiClient.createWorkflow({
          name: suggestion.name || 'Untitled Workflow',
          description: suggestion.description || '',
          definition: suggestion.definition,
          trigger_config: suggestion.trigger_config || undefined,
          nl_prompt: suggestion.description || '',
          simulated: options?.simulated || undefined,
        });
        await linkRunbooks(enabledRunbookIds, wf.id);
        navigate(`/workflows/${wf.id}/edit`);
        return;
      }

      // Fallback: legacy format — build a prompt and call the builder agent
      const details: string[] = [`Create a workflow called "${suggestion.name}".`];
      if (suggestion.description) details.push(`Description: ${suggestion.description}`);
      if (suggestion.trigger) {
        details.push(`Trigger: source=${suggestion.trigger.source}, signal_type=${suggestion.trigger.signal_type}${suggestion.trigger.description ? `, description: ${suggestion.trigger.description}` : ''}`);
      }
      if (suggestion.actions?.length) {
        details.push('Actions (in order):');
        suggestion.actions.forEach((a: any, i: number) => {
          let actionDetail = `  ${i + 1}. ${a.action_id || 'unknown'}`;
          if (a.label) actionDetail += ` — "${a.label}"`;
          if (a.config) actionDetail += ` config: ${JSON.stringify(a.config)}`;
          details.push(actionDetail);
        });
      }
      const prompt = details.join('\n');

      const generated = await apiClient.generateWorkflow(prompt, options?.simulated);

      if (!generated.success || !generated.definition) {
        console.error('Workflow generation failed:', generated.error);
        return;
      }

      const wf = await apiClient.createWorkflow({
        name: generated.name || suggestion.name || 'Untitled Workflow',
        description: generated.description || suggestion.description || '',
        definition: generated.definition,
        trigger_config: generated.trigger_config,
        nl_prompt: prompt,
        simulated: options?.simulated || undefined,
      });
      await linkRunbooks(enabledRunbookIds, wf.id);
      navigate(`/workflows/${wf.id}/edit`);
    } catch (err: any) {
      const status = err?.response?.status || err?.status;
      const msg = err?.response?.data?.message || err?.message || '';
      if (status === 409 || msg.includes('already exists')) {
        const defaultName = (suggestion.name || 'Untitled Workflow') + ' (copy)';
        setRenameName(defaultName);
        setRenameDialog({ suggestion, enabledRunbookIds, defaultName });
      } else {
        console.error('Failed to create workflow draft:', err);
      }
    }
  };

  const createRenamed = async () => {
    if (!renameDialog || !renameName.trim()) return;
    try {
      const wf = await apiClient.createWorkflow({
        name: renameName.trim(),
        description: renameDialog.suggestion.description || '',
        definition: renameDialog.suggestion.definition || undefined,
        trigger_config: renameDialog.suggestion.trigger_config || undefined,
        nl_prompt: renameDialog.suggestion.description || '',
        simulated: options?.simulated || undefined,
      });
      setRenameDialog(null);
      navigate(`/workflows/${wf.id}/edit`);
    } catch {}
  };

  // Rename workflow dialog (on 409 conflict)
  const renameDialogElement = renameDialog ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRenameDialog(null)}>
      <div className="bg-background border rounded-lg shadow-xl max-w-md w-full flex flex-col m-4" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-medium">Workflow name already exists</h3>
          <p className="text-xs text-muted-foreground mt-1">A workflow with this name already exists. Enter a new name:</p>
        </div>
        <div className="px-4 py-3">
          <input
            type="text"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && renameName.trim()) {
                createRenamed();
              }
            }}
          />
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t">
          <Button variant="outline" size="sm" onClick={() => setRenameDialog(null)}>Cancel</Button>
          <Button size="sm" onClick={createRenamed}>Create</Button>
        </div>
      </div>
    </div>
  ) : null;

  return { saveSuggestion, createSuggestion, renameDialogElement };
}
